// Permission utility functions for RBAC

export interface Permission {
  id: string;
  policy_ref: string;
  effect: string;
  attributes: any;
}

export interface Role {
  id: string;
  key: string;
  name: string;
  description?: string;
  is_admin?: boolean;
  is_delegated_admin?: boolean;
}

/** The backend's own verdict on the signed-in user (GET /auth/userinfo). */
export interface AdminFlags {
  is_admin?: boolean;
  is_delegated_admin?: boolean;
}

// Admin is decided by explicit flags and exact role keys/names only (design
// eval F27). It used to be any role whose name merely contained "admin" or
// "delegated", so "Admin Viewer" or "Non-delegated Ops" unlocked the console.
// These are the built-in admin roles; a custom admin role carries is_admin.
// The backend must enforce every check here too: this only decides what the
// console draws and routes to.
export const ADMIN_ROLE_KEYS: ReadonlySet<string> = new Set(['admin', 'super_admin', 'delegated_admin']);
export const ADMIN_ROLE_NAMES: ReadonlySet<string> = new Set(['admin', 'administrator', 'super admin', 'delegated admin']);

// Check if user has admin role
export const isAdmin = (activeRole: Role | null | undefined): boolean => {
  if (!activeRole) return false;
  if (activeRole.is_admin === true || activeRole.is_delegated_admin === true) return true;
  const key = (activeRole.key || '').trim().toLowerCase();
  const name = (activeRole.name || '').trim().toLowerCase();
  return ADMIN_ROLE_KEYS.has(key) || ADMIN_ROLE_NAMES.has(name);
};

// Check if any role in the list is an admin role
export const hasAdminRole = (roles: Role[] | null | undefined): boolean => {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => isAdmin(role));
};

/** Admin by the backend's flags, the active role, or any held role. */
export const isAdminUser = (
  flags: AdminFlags | null | undefined,
  activeRole: Role | null | undefined,
  allRoles: Role[] = [],
): boolean =>
  flags?.is_admin === true || flags?.is_delegated_admin === true || isAdmin(activeRole) || hasAdminRole(allRoles);

// Check if user has viewer role (by key)
export const isViewer = (activeRole: Role | null): boolean => {
  if (!activeRole) return false;
  return activeRole.key === 'viewer';
};

// Check if any role in the list is a viewer role
export const hasViewerRole = (roles: Role[]): boolean => {
  if (!roles || roles.length === 0) return false;
  return roles.some(role => role.key === 'viewer');
};

// Check if user can view resources (admin or viewer)
export const canViewResources = (activeRole: Role | null, roles: Role[]): boolean => {
  if (isAdmin(activeRole) || hasAdminRole(roles)) return true;
  if (isViewer(activeRole) || hasViewerRole(roles)) return true;
  return false;
};

// Check if user can manage resources (admin only)
export const canManageResources = (activeRole: Role | null, roles: Role[]): boolean => {
  return isAdmin(activeRole) || hasAdminRole(roles);
};

// Check if user has a specific permission
export const hasPermission = (
  permissions: Permission[],
  policyRef: string,
  activeRole: Role | null,
  allRoles: Role[] = []
): boolean => {
  // Admin bypasses all permission checks (check activeRole first, then all roles)
  if (isAdmin(activeRole) || hasAdminRole(allRoles)) {
    return true;
  }

  // Check if permission exists and effect is 'allow'
  return permissions.some(
    (p) => p.policy_ref === policyRef && p.effect === 'allow'
  );
};

// Check if user has any of the specified permissions
export const hasAnyPermission = (
  permissions: Permission[],
  policyRefs: string[],
  activeRole: Role | null,
  allRoles: Role[] = []
): boolean => {
  // Admin bypasses all permission checks
  if (isAdmin(activeRole) || hasAdminRole(allRoles)) {
    return true;
  }

  return policyRefs.some((ref) => hasPermission(permissions, ref, activeRole, allRoles));
};

// Check if user has all of the specified permissions
export const hasAllPermissions = (
  permissions: Permission[],
  policyRefs: string[],
  activeRole: Role | null,
  allRoles: Role[] = []
): boolean => {
  // Admin bypasses all permission checks
  if (isAdmin(activeRole) || hasAdminRole(allRoles)) {
    return true;
  }

  return policyRefs.every((ref) => hasPermission(permissions, ref, activeRole, allRoles));
};

// Permission mappings for different resources and actions
export const PERMISSIONS = {
  // Users
  USERS_LIST: 'users:list',
  USERS_GET: 'users:get',
  USERS_CREATE: 'users:create',
  USERS_UPDATE: 'users:update',
  USERS_UPDATE_STATUS: 'users:update_status',
  USERS_DELETE: 'users:delete',

  // Groups
  GROUPS_LIST: 'groups:list',
  GROUPS_GET: 'groups:get',
  GROUPS_CREATE: 'groups:create',
  GROUPS_UPDATE: 'groups:update',
  GROUPS_DELETE: 'groups:delete',
  GROUPS_MANAGE_MEMBERS: 'groups:manage_members',

  // Roles
  ROLES_LIST: 'roles:list',
  ROLES_GET: 'roles:get',
  ROLES_CREATE: 'roles:create',
  ROLES_UPDATE: 'roles:update',
  ROLES_DELETE: 'roles:delete',

  // Resources
  RESOURCES_LIST: 'resources:list',
  RESOURCES_GET: 'resources:get',
  RESOURCES_CREATE: 'resources:create',
  RESOURCES_UPDATE: 'resources:update',
  RESOURCES_DELETE: 'resources:delete',

  // Sessions
  SESSIONS_LIST: 'sessions:list',
  SESSIONS_GET: 'sessions:get',
  SESSIONS_TERMINATE: 'sessions:terminate',

  // Access Logs
  ACCESS_LOGS_LIST: 'access_logs:list',
  ACCESS_LOGS_GET: 'access_logs:get',

  // Policies
  POLICIES_LIST: 'policies:list',
  POLICIES_GET: 'policies:get',
  POLICIES_CREATE: 'policies:create',
  POLICIES_UPDATE: 'policies:update',
  POLICIES_DELETE: 'policies:delete',

  // Policy Bindings
  POLICY_BINDINGS_LIST: 'policy_bindings:list',
  POLICY_BINDINGS_CREATE: 'policy_bindings:create',
  POLICY_BINDINGS_DELETE: 'policy_bindings:delete',

  // Role Bindings
  ROLE_BINDINGS_LIST: 'role_bindings:list',
  ROLE_BINDINGS_CREATE: 'role_bindings:create',
  ROLE_BINDINGS_DELETE: 'role_bindings:delete',

  // Access Requests
  ACCESS_REQUESTS_LIST: 'access_requests:list',
  ACCESS_REQUESTS_GET: 'access_requests:get',
  ACCESS_REQUESTS_CREATE: 'access_requests:create',
  ACCESS_REQUESTS_APPROVE: 'access_requests:approve',
  ACCESS_REQUESTS_REJECT: 'access_requests:reject',
  ACCESS_REQUESTS_DELETE: 'access_requests:delete',
};

// Routes only an admin may open. The nav hides them and the router guards them
// (components/RequireRoute.tsx) from this one list, so typing a URL can't reach
// a page the nav withholds (design eval F27). A prefix covers its sub-routes:
// /sessions covers the player and the join page.
export const ADMIN_ONLY_ROUTES: readonly string[] = [
  '/resources',
  '/sessions',
  '/access-logs',
  '/settings',
  '/trusted-clusters',
];

const matchesRoute = (route: string, base: string) => route === base || route.startsWith(base + '/');

// Check if user can access a specific page/route
export const canAccessRoute = (
  route: string,
  permissions: Permission[],
  activeRole: Role | null,
  allRoles: Role[] = [],
  flags?: AdminFlags | null,
): boolean => {
  // Admin can access everything: the backend's is_admin / is_delegated_admin,
  // the active role or any held role, exactly as usePermissions().isAdmin().
  if (isAdminUser(flags, activeRole, allRoles)) {
    return true;
  }

  if (ADMIN_ONLY_ROUTES.some((r) => matchesRoute(route, r))) {
    return false; // Non-admin users cannot access these routes
  }

  // Map routes to required permissions
  // Empty array = accessible to all authenticated users
  const routePermissions: Record<string, string[]> = {
    '/dashboard': [],
    '/users': [], // User Management - visible to all, actions controlled by PermissionGate
    '/groups': [],
    '/roles': [],
    '/proxy-resources': [],
    '/ssh-resources': [],
    '/k8s-resources': [],
    '/ssh-activity-logs': [],
    '/access-requests': [],
    '/policies': [],
    // Getting Started is static documentation and the list of guided tours —
    // no live data, no secrets (every token in it is a placeholder). Everyone
    // gets it: the people most likely to need the tours are the ones with the
    // fewest permissions, and withholding it left them with no help button on
    // any page without a tour of its own.
    '/setup-guide': [],
  };

  const requiredPerms = routePermissions[route];

  // If route not found or no permissions required, allow access
  if (!requiredPerms || requiredPerms.length === 0) {
    return true;
  }

  // Check if user has any of the required permissions
  return hasAnyPermission(permissions, requiredPerms, activeRole, allRoles);
};
